import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Draft store for the 14-section visit form.
 * Survives accidental refreshes / tab closes via localStorage.
 *
 * Keyed by patient_id so multiple in-flight drafts don't collide:
 *   drafts[patient_id] = { stepIndex, data: { vitals: {...}, ... } }
 *
 * For the "new patient" flow before an ID exists, we use the key `__new__`.
 */
export const useVisitDraftStore = create(
  persist(
    (set, get) => ({
      drafts: {},

      getDraft: (patientId = '__new__') =>
        get().drafts[patientId] || { stepIndex: 0, data: {} },

      setStepIndex: (patientId, stepIndex) =>
        set((s) => {
          const cur = s.drafts[patientId] || { stepIndex: 0, data: {} }
          return { drafts: { ...s.drafts, [patientId]: { ...cur, stepIndex } } }
        }),

      setSectionData: (patientId, sectionKey, value) =>
        set((s) => {
          const cur = s.drafts[patientId] || { stepIndex: 0, data: {} }
          return {
            drafts: {
              ...s.drafts,
              [patientId]: {
                ...cur,
                data: { ...cur.data, [sectionKey]: value },
              },
            },
          }
        }),

      /** Migrate a draft from the placeholder `__new__` key to a real patient_id
       *  after the patient is created. */
      promoteNewDraft: (newPatientId) =>
        set((s) => {
          const tmp = s.drafts.__new__
          if (!tmp) return s
          const next = { ...s.drafts }
          delete next.__new__
          next[newPatientId] = tmp
          return { drafts: next }
        }),

      clearDraft: (patientId) =>
        set((s) => {
          const next = { ...s.drafts }
          delete next[patientId]
          return { drafts: next }
        }),
    }),
    { name: 'rd_visit_drafts' },
  ),
)
